import { useMemo } from 'react';
import {
  useActiveAccount,
  useActiveWallet,
  useActiveWalletChain,
  useSwitchActiveWalletChain,
} from 'thirdweb/react';

import type { JsonRpcProvider } from '@ethersproject/providers';
import { useConnector, useEthers } from '@usedapp/core';
import { ethers5Adapter } from 'thirdweb/adapters/ethers5';
import { EIP1193 } from 'thirdweb/wallets';

import { client, getThirdwebChain } from '@context/thirdweb';

/**
 * Swap-local replacement for @usedapp/core's useEthers(), sourced directly
 * from thirdweb's own connection state instead of the app-wide usedapp
 * bridge. `library` is a standard ethers v5 read-only Provider produced via
 * thirdweb's official ethers5Adapter, so existing ethers-based swap logic
 * (trade SDKs, receipt parsing, raw contract reads) keeps working unchanged.
 *
 * Falls back to @usedapp/core's own useEthers() wherever thirdweb's own
 * session isn't present. This matters because DAppProvider's legacy
 * MetamaskConnector still auto-reconnects independently of thirdweb
 * (autoConnect: true) — a browser with a pre-thirdweb usedapp session shows
 * "connected" in the header (which already falls back the same way, see
 * LoginButton.tsx) even though thirdweb's own useActiveAccount() never
 * resolves. Without this fallback Swap would show a redundant "Connect
 * wallet" prompt whenever that happens.
 */
export const useSwapWallet = () => {
  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const activeChain = useActiveWalletChain();
  const switchActiveWalletChain = useSwitchActiveWalletChain();

  const {
    account: legacyAccount,
    chainId: legacyChainId,
    library: legacyLibrary,
    switchNetwork: legacySwitchNetwork,
  } = useEthers();
  const { connector: legacyConnector } = useConnector();

  const chainId = activeChain?.id ?? legacyChainId;

  // Replaces the legacy isMetaMask()/isWalletConnect() helpers, which read
  // library.connection.url — a JsonRpcProvider-specific field the
  // ethers5Adapter's plain read-only Provider doesn't have (accessing it
  // would throw). thirdweb's own wallet id is a direct, reliable signal;
  // falls back to the legacy connector's name when only usedapp is connected.
  const legacyConnectorName =
    legacyConnector?.connector?.name?.toLowerCase() ?? '';
  const isMetaMask = wallet
    ? wallet.id === 'io.metamask'
    : legacyConnectorName === 'metamask';
  const isWalletConnect = wallet
    ? wallet.id !== 'io.metamask' && wallet.id !== 'io.rabby'
    : legacyConnectorName === 'walletconnect' ||
      legacyConnectorName === 'wcuniversal';

  const library = useMemo(() => {
    if (client && activeChain) {
      return ethers5Adapter.provider.toEthers({ client, chain: activeChain });
    }
    return legacyLibrary;
  }, [activeChain, legacyLibrary]);

  // The wallet's own raw EIP-1193 provider — distinct from `library` (a
  // read-only RPC provider with no relation to the connected wallet).
  // Needed for wallet-specific RPC calls (wallet_watchAsset,
  // wallet_switchEthereumChain, ...) and for handing off to third-party
  // widgets that need the real connected-wallet provider (e.g. DlnWidget).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const walletProvider = useMemo((): any => {
    if (client && wallet && activeChain) {
      return EIP1193.toProvider({ wallet, chain: activeChain, client });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (legacyLibrary as any)?.provider;
  }, [wallet, activeChain, legacyLibrary]);

  const switchNetwork = useMemo(
    () => async (targetChainId: number) => {
      if (wallet && activeChain) {
        await switchActiveWalletChain(getThirdwebChain(targetChainId));
        return;
      }
      await legacySwitchNetwork(targetChainId);
    },
    [switchActiveWalletChain, wallet, activeChain, legacySwitchNetwork],
  );

  // Lazily builds a write-capable ethers signer for one-off sends outside
  // the SignerInstanceHandler-tracked flow (e.g. MetaButton's own transfer).
  const getSigner = useMemo(
    () => async () => {
      if (client && account && activeChain) {
        return ethers5Adapter.signer.toEthers({
          client,
          chain: activeChain,
          account,
        });
      }
      if (!legacyAccount || !legacyLibrary) return undefined;
      // usedapp only ever populates `library` with a signer-capable
      // JsonRpcProvider (Web3Provider) for an actual connected wallet; the
      // FallbackProvider variant of useEthers()'s type is for the
      // read-only/disconnected case, ruled out by the legacyAccount check.
      return (legacyLibrary as JsonRpcProvider).getSigner(legacyAccount);
    },
    [account, activeChain, legacyLibrary, legacyAccount],
  );

  return {
    account: account?.address || legacyAccount,
    chainId,
    library,
    walletProvider,
    switchNetwork,
    isMetaMask,
    isWalletConnect,
    getSigner,
  };
};
